import React from 'react';

import {
  useGetAssociationMemberRequests,
  useUpdateMemberStatus
} from 'legacy/groups/hooks';
import Loader from 'legacy/common/components/layout/Loader';
import * as toast from 'legacy/common/utils/toast';
import { Avatar } from 'components/Avatar';
import MemberList from 'components/lists/MemberList';
import MembersListItem from 'components/lists/MemberList/ListItem';
import { useTranslation } from 'react-i18next';

interface Props {
  associationId: string;
  search: string;
}

const RequestsList = ({ associationId, search }: Props) => {
  const { t } = useTranslation(['associations']);
  const {
    isLoading,
    data,
    fetchNextPage,
    fetchPreviousPage,
    hasPreviousPage,
    hasNextPage
  } = useGetAssociationMemberRequests(associationId, search);
  const { mutate, isLoading: isUpdatingStatus } = useUpdateMemberStatus();

  const acceptRequest = (request) => {
    mutate(
      { associationId, userId: request.user.id, status: 'approved' },
      {
        onSuccess: () => {
          toast.success(t('membershipRequestApproved'));
        },
        onError: () => {
          toast.error(t('membershipRequestApprovedError'));
        }
      }
    );
  };

  const rejectRequest = (request) => {
    mutate(
      { associationId, userId: request.user.id, status: 'rejected' },
      {
        onSuccess: () => {
          toast.success(t('membershipRequestRejected'));
        },
        onError: () => {
          toast.error(t('membershipRequestRejectedError'));
        }
      }
    );
  };

  return (
    <MemberList
      title={t('members.requests')}
      description={t('members.requestsDescription')}
      onNext={fetchNextPage}
      onPrev={fetchPreviousPage}
      hasNextPage={hasNextPage}
      hasPreviousPage={hasPreviousPage}
      emptyText={t('members.noRequests')}
    >
      {isLoading ? (
        <Loader isLoading />
      ) : (
        data?.map((request) => (
          <MembersListItem
            key={request.id}
            avatar={<Avatar src={request.user.photoUrl} />}
            label={`${request.user.firstName} ${request.user.lastName}`}
            actions={
              <>
                <button
                  className="text-primary-700 text-sm font-bold"
                  aria-label="Reject request"
                  type="button"
                  disabled={isUpdatingStatus}
                  onClick={() => rejectRequest(request)}
                >
                  {t('members.reject')}
                </button>
                <div className="text-neutral-300">|</div>
                <button
                  className="text-primary-700 text-sm font-bold"
                  aria-label="Accept request"
                  type="button"
                  disabled={isUpdatingStatus}
                  onClick={() => acceptRequest(request)}
                >
                  {t('members.accept')}
                </button>
              </>
            }
          />
        ))
      )}
    </MemberList>
  );
};

export default RequestsList;
